/* ------------------------ Functional imports start ------------------------ */
import { type PermissionDetailsType, type PermissionType } from './types'
import {
  type APIRole,
  APIRoleFromJSON,
  type APIAllInOneUser,
  APIAllInOneUserFromJSON,
} from '../../../generated-types'
import LogTool from '../../../logger/logTools'
import { apiClient, type PaginatedResponse } from '../../../api/client'
import { buildFetchUrl, getPKfromSelf } from '../../../utils/functions'
import { type Role, type User, type UserPreferences } from '../../../utils/types'
import { PERMISSION_SWITCH_LINKS } from '../../../config/permissions'
import { getApiBasePath } from '../../../api/authTokens'

/* ------------------------- Functional imports end ------------------------- */

const log = new LogTool({ context: 'EmployeeUtils', enable: true, logLevel: 'info' })

/* -------------------------------------------------------------------------- */
/*                           Utility functions start                          */
/* -------------------------------------------------------------------------- */
export function createUser(userResponse: any): User {
  const apiUser: APIAllInOneUser = APIAllInOneUserFromJSON(userResponse)

  return {
    ...apiUser,
    key: getPKfromSelf(apiUser.self),
    roles: apiUser.roles,
    preferences: apiUser.preferences as unknown as UserPreferences
  }
}

export function createRole(roleResponse: any): Role {
  const apiRole: APIRole = APIRoleFromJSON(roleResponse)
  return {
    ...apiRole,
    key: getPKfromSelf(apiRole.self),
  }
}

/**
 * Convert a role object/ID to a role URL string for API requests
 * Handles various input formats:
 * - {value: {self: "..."}} -> returns the self URL (already full URL)
 * - {value: "4"} or {value: 4} -> constructs full URL "http://localhost:8000/v1/accounts/roles/4/"
 * - {value: "v1/accounts/roles/4/"} -> constructs full URL with API base
 * - {self: "..."} -> returns the self URL (already full URL)
 * - "v1/accounts/roles/4/" -> constructs full URL with API base
 * - "http://localhost:8000/v1/accounts/roles/4/" -> returns as-is (already full URL)
 */
export function roleToUrlString(role: any): string {
  // If role.value is an object with self property, use it (should already be full URL)
  if (role?.value?.self && typeof role.value.self === 'string') {
    return role.value.self
  }
  // If role.value is a string/number (ID), construct the full URL
  if (role?.value && (typeof role.value === 'string' || typeof role.value === 'number')) {
    // Check if it's already a full URL
    if (typeof role.value === 'string' && (role.value.startsWith('http://') || role.value.startsWith('https://'))) {
      return role.value
    }
    // Otherwise construct full URL from ID
    const basePath = getApiBasePath()
    return `${basePath}/v1/accounts/roles/${role.value}/`
  }
  // If role.value is already a URL string, check if it's full or relative
  if (role?.value && typeof role.value === 'string' && role.value.includes('/')) {
    // If it's already a full URL, return as-is
    if (role.value.startsWith('http://') || role.value.startsWith('https://')) {
      return role.value
    }
    // Otherwise prepend API base path
    const basePath = getApiBasePath()
    return `${basePath}/${role.value.startsWith('/') ? role.value.slice(1) : role.value}`
  }
  // If role itself has a self property, use it (should already be full URL)
  if (role?.self && typeof role.self === 'string') {
    return role.self
  }
  // If role is a URL string, check if it's full or relative
  if (typeof role === 'string' && role.includes('/')) {
    // If it's already a full URL, return as-is
    if (role.startsWith('http://') || role.startsWith('https://')) {
      return role
    }
    // Otherwise prepend API base path
    const basePath = getApiBasePath()
    return `${basePath}/${role.startsWith('/') ? role.slice(1) : role}`
  }
  // Fallback: if role is an object, try to get self from it
  if (role && typeof role === 'object') {
    return role.self || role
  }
  // Last resort fallback
  return role
}

export function inputToAPIAllInOneUserJSON(
  input: any,
  options: { includeRoles?: boolean } = {}
) {
  const payload: Record<string, any> = {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    ...(input?.firstName !== undefined && { first_name: input.firstName }),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    ...(input?.lastName !== undefined && { last_name: input.lastName }),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    ...(input?.email !== undefined && { email: input.email }),
    // Always include is_active as a boolean value. Default to false if not specified.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    is_active: input?.isActive !== undefined ? Boolean(input.isActive) : false,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    ...(input?.comment !== undefined && { comment: input.comment }),
  }

  if (options.includeRoles && Array.isArray(input?.roles)) {
    payload.roles = input.roles.map(roleToUrlString)
  }

  return payload
}

export const getTablePermissions = (permissions: any): PermissionType[] => {
  const seenTypes = new Set<string>()
  const tablePermissions: PermissionType[] = []

  permissions.forEach((permission: any) => {
    const slug: string | undefined =
      typeof permission.slug === 'string' && permission.slug.length > 0
        ? permission.slug
        : permission.name
    if (!slug) {
      return
    }
    const [type] = slug.split(':')
    if (!type || seenTypes.has(type)) {
      return
    }
    seenTypes.add(type)
    tablePermissions.push({
      key: type,
      type,
    })
  })

  return tablePermissions
}

export const getTablePermissionDetails = (permissions: any): PermissionDetailsType[] => {
  return permissions
    .map((permission: any, index: number) => {
      const slug: string | undefined =
        typeof permission.slug === 'string' && permission.slug.length > 0
          ? permission.slug
          : permission.name
      if (!slug) {
        return undefined
      }
      const [type, ...nameParts] = slug.split(':')
      if (!type) {
        return undefined
      }
      return {
        key: slug ?? index,
        type,
        name: nameParts,
        slug,
        description: permission.description ?? '',
        operations: [],
      }
    })
    .filter((entry: any): entry is PermissionDetailsType => Boolean(entry))
}

const PERMISSION_SWITCH_CANONICAL_LOOKUP: Record<string, string> = Object.entries(
  PERMISSION_SWITCH_LINKS
).reduce((acc, [canonical, slugs]) => {
  slugs.forEach(slug => {
    acc[slug] = canonical
  })
  if (!acc[canonical]) {
    acc[canonical] = canonical
  }
  return acc
}, {} as Record<string, string>)

const PERMISSION_SWITCH_HIDDEN_SLUGS = new Set(
  Object.entries(PERMISSION_SWITCH_LINKS).flatMap(([canonical, slugs]) =>
    slugs.filter(slug => slug !== canonical)
  )
)

export const getCanonicalPermissionSlug = (slug: string): string =>
  PERMISSION_SWITCH_CANONICAL_LOOKUP[slug] ?? slug

export const getLinkedPermissionSlugs = (slug: string): string[] => {
  const canonical = getCanonicalPermissionSlug(slug)
  return PERMISSION_SWITCH_LINKS[canonical] ?? [canonical]
}

export const shouldHideCombinedPermission = (slug?: string): boolean =>
  slug ? PERMISSION_SWITCH_HIDDEN_SLUGS.has(slug) : false

export const FULL_OPERATIONS: string[] = ['edit', 'view', 'create', 'delete']

const OPERATIONS_REQUIRING_VIEW = new Set<string>(['edit', 'create', 'delete'])

export const mergeOperationsWithViewRequirement = (
  existingOperations: string[],
  operationsToAdd: string[]
): string[] => {
  const operationSet = new Set(existingOperations)
  operationsToAdd.forEach(operation => {
    if (!FULL_OPERATIONS.includes(operation)) {
      return
    }
    operationSet.add(operation)
    if (OPERATIONS_REQUIRING_VIEW.has(operation)) {
      operationSet.add('view')
    }
  })
  return FULL_OPERATIONS.filter(operation => operationSet.has(operation))
}

export const removeOperationsWithViewRequirement = (
  existingOperations: string[],
  operationsToRemove: string[]
): string[] => {
  const removalSet = new Set(operationsToRemove)
  const remainingOperations = existingOperations.filter(operation => !removalSet.has(operation))
  return remainingOperations.includes('view') ? remainingOperations : []
}

export const buildPermissionKey = (type: string, name: string[], slug?: string): string => {
  if (slug && slug.length > 0) {
    return slug
  }
  const parts = [type, ...name].filter(part => part && part.length > 0)
  return parts.join(':')
}

export const getDetailPermissionKey = (detail: PermissionDetailsType): string =>
  getCanonicalPermissionSlug(buildPermissionKey(detail.type, detail.name, detail.slug))

/* -------------------------------------------------------------------------- */
/*                             API functions start                            */
/* -------------------------------------------------------------------------- */

async function fetchCurrentOrganization(): Promise<string> {
  try {
    const response = await apiClient.get<any>('v1/accounts/users/self/')
    log.info('Success fetching current organization for URL', response.organization)
    return response.organization
  } catch (error) {
    log.error('Error fetching current organization', error)
    return Promise.reject(error)
  }
}

/**
 * Fetch user data from backend. Note that this fetches all pages of a paginated response sequentially.
 * @param onSuccess Callback that is executed every time when receiving a response from the server.
 * @param onFinal Callback that is executed after calling onSuccess for the last time.
 * @param onError Callback that is executed every time when receiving an error from the server.
 * @param parameter Fetch parameter, e.g. expand, exclude, ... . Written as ['expand=supplier,article']
 * @param url The fetch url.
 * @returns
 */
export async function fetchUsers(
  onSuccess: (users: User[], count: number) => void,
  onFinal: () => void = () => null,
  onError: (error: any) => void = error => null,
  parameter: string[] = [],
  url = 'v1/accounts/users/',
  suppressWarning = false,
  currentOrganization?: string
): Promise<void> {
  if (!suppressWarning) {
    log.warn(
      'fetchUsers is deprecated. Please migrate to useUsers() from features/employee/hooks/useEmployeeQueries.'
    )
  }

  log.info('Begin fetching users.')
  if (!url.match(/v1\/accounts\/users\//)) {
    log.error(`The provided URL ${url} does not lead to the correct API endpoint!`)
    return
  }

  const fetchUrl = buildFetchUrl(url, parameter)

  try {
    const response = await apiClient.get<
      PaginatedResponse<any> & { results?: any[]; next?: string | null; count?: number }
    >(fetchUrl)

    log.info('Success fetching users for URL', fetchUrl)

    const rawResults = Array.isArray(response?.results) ? response.results : []
    const users = rawResults.map((userResponse: any): User => createUser(userResponse))
    const count =
      typeof response?.count === 'number'
        ? response.count
        : users.length

    const organization =
      currentOrganization !== undefined
        ? currentOrganization
        : await fetchCurrentOrganization()

    const filteredUsers =
      typeof organization === 'string'
        ? users.filter((user: User) => user.organization === organization)
        : users

    if (response?.next) {
      await fetchUsers(onSuccess, onFinal, onError, parameter, response.next, true, organization)
    }

    onSuccess(filteredUsers, count)
    if (!response?.next) {
      onFinal()
    }
  } catch (error) {
    log.error('Error fetching users', error)
    onError(error)
  }
}

async function fetchUser(user: string): Promise<User> {
  log.info('Begin fetching user.')
  if (!user) {
    return Promise.reject('No user provided!')
  }

  const separator = user.includes('?') ? '&' : '?'
  const fetchUrl = `${user}${separator}expand=roles__sub_roles`

  try {
    const response = await apiClient.get<any>(fetchUrl)
    log.info('Success fetching user for URL', response?.self ?? fetchUrl)
    return createUser(response)
  } catch (error) {
    log.error('Error fetching user', error)
    return Promise.reject(error)
  }
}

export async function fetchLoggedInUser(
  onSuccess: (user: User) => void,
  onError: (error: any) => void = error => null,
  parameter: string[] = []
): Promise<void> {
  log.warn(
    'fetchLoggedInUser is deprecated. Please migrate to useSelfUser() from features/employee/hooks/useEmployeeQueries.'
  )
  log.info('Begin fetching logged in user.')
  // check if the url leads to the correct API endpoint
  const url = 'v1/accounts/users/self/'

  // build the complete fetch url
  const fetchUrl = buildFetchUrl(url, parameter)

  try {
    const response = await apiClient.get<any>(fetchUrl)
    log.info('Success fetching logged in user for URL', fetchUrl)
    const user = createUser(response)
    onSuccess(user)
  } catch (error) {
    log.error('Error fetching logged in user', error)
    onError(error)
  }
}

export async function postUser(
  user: User,
  onSuccess: (user: User) => void,
  onError: (error: any) => void = error => null
): Promise<void> {
  log.warn(
    'postUser is deprecated. Please migrate to useCreateUser() from features/employee/hooks/useEmployeeMutations.'
  )
  log.info('Begin posting user.')

  try {
    const json = inputToAPIAllInOneUserJSON(user, { includeRoles: true })
    const response = await apiClient.post<any>('v1/accounts/users/', json)

    log.info('Success posting user for URL', response?.self ?? 'v1/accounts/users/')
    const createdUser =
      response?.self !== undefined
        ? await fetchUser(response.self)
        : createUser(response)

    onSuccess(createdUser)
  } catch (error) {
    log.error('Error posting user', error)
    onError(error)
  }
}

export async function updateUser(
  user: User,
  onSuccess: (user: User) => void,
  onError: (error: any) => void = error => null
): Promise<void> {
  log.warn(
    'updateUser is deprecated. Please migrate to useUpdateUser() from features/employee/hooks/useEmployeeMutations.'
  )
  log.info('Begin updating user.')

  try {
    const json = inputToAPIAllInOneUserJSON(user)
    const response = await apiClient.put<any>(user.self, json)

    log.info('Success updating user for URL', response?.self ?? user.self)
    const updatedUser =
      response?.self !== undefined
        ? await fetchUser(response.self)
        : createUser(response)

    onSuccess(updatedUser)
  } catch (error) {
    log.error('Error updating user', error)
    onError(error)
  }
}

export async function updateUserRoles(
  user: User,
  roles: any[],
  onSuccess: (user: User, roles: any[]) => void,
  onError: (error: any) => void = error => null
): Promise<void> {
  log.warn(
    'updateUserRoles is deprecated. Please migrate to useUpdateUserRoles() from features/employee/hooks/useEmployeeMutations.'
  )
  log.info('Begin updating user roles.')
  if (!Array.isArray(roles)) return
  const json = {
    roles: roles.map(roleToUrlString),
  }
  try {
    await apiClient.put(`${user.self}roles/`, json)
    log.info('Success updating user roles for URL', user.self)
    const newRoles = roles.map((role: any) => (role.value ? role.value : role))
    onSuccess(user, newRoles)
  } catch (error) {
    log.error('Error updating user roles', error)
    onError(error)
  }
}

export async function deleteUsers(
  users: User[],
  loggedInUser: User,
  onSuccess: () => void,
  onError: (error: any) => void = error => null
): Promise<void> {
  log.warn(
    'deleteUsers is deprecated. Please migrate to useDeleteUsers() from features/employee/hooks/useEmployeeMutations.'
  )
  log.info('Begin deleting user.')
  if (users.map(user => user.self).includes(loggedInUser.self)) {
    log.error('Error deleting user', 'You cannot delete yourself!')
    onError('cannotDeleteSelf')
    return
  }
  const promises = users.map(user => deleteUser(user))
  const results = await Promise.all(promises)
  if (!results.includes(false)) {
    onSuccess()
  } else {
    onError('Error deleting users!')
  }
}

async function deleteUser(user: User): Promise<boolean> {
  log.info('Begin deleting user.')
  try {
    await apiClient.delete(user.self)
    log.info('Success deleting user for URL', user.self)
    return true
  } catch (error) {
    log.error('Error deleting user', error)
    return false
  }
}
